import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { BoltIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { addFastTrackContact, getCompanies } from '../../../../services/firebase';
import ContactSelector from './ContactSelector';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { toast } from 'react-hot-toast';

const FastTrackContactModal = ({ jobId, companyId, onClose, onContactAdded }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  
  // Contact state
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactSignalStrength, setContactSignalStrength] = useState(2);
  const [contactAvailableTimes, setContactAvailableTimes] = useState('');
  const [contactNotes, setContactNotes] = useState('');
  const [originalContact, setOriginalContact] = useState(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  
  // Fetch list of companies
  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        // If we already have a companyId, fetch just that company
        if (companyId) {
          const db = getFirestore();
          const companyRef = doc(db, 'companies', companyId);
          const companySnap = await getDoc(companyRef);
          
          if (companySnap.exists()) {
            const companyData = companySnap.data();
            setSelectedCompany({
              id: companyId,
              name: companyData.name || '',
              logo: companyData.logo || null
            });
            setCompanyName(companyData.name || '');
            setCompanySearchQuery(companyData.name || '');
          }
        } else {
          // Otherwise fetch all companies
          const db = getFirestore();
          const companiesRef = collection(db, 'companies');
          const companiesQuery = query(
            companiesRef,
            orderBy('name'),
            limit(100)
          );
          
          const querySnapshot = await getDocs(companiesQuery);
          const companiesList = [];
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.visible !== false) { // Only include visible companies
              companiesList.push({
                id: doc.id,
                name: data.name || '',
                logo: data.logo || null,
                industry: data.industry || '',
                size: data.size || ''
              });
            }
          });
          
          setCompanies(companiesList);
          setFilteredCompanies(companiesList);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setIsLoadingCompanies(false);
      }
    };
    
    fetchCompanies();
  }, [companyId]);
  
  // Filter companies based on search query
  useEffect(() => {
    if (!companySearchQuery.trim() || !companies.length) {
      setFilteredCompanies(companies);
      return;
    }
    
    const query = companySearchQuery.toLowerCase();
    const filtered = companies.filter(company => {
      return company.name.toLowerCase().includes(query) ||
             (company.industry && company.industry.toLowerCase().includes(query));
    });
    
    setFilteredCompanies(filtered);
  }, [companySearchQuery, companies]);
  
  const handleContactSelect = (contact) => {
    if (!contact) {
      console.error('Received null or undefined contact in handleContactSelect');
      return;
    }
    
    setOriginalContact(contact);
    setContactFirstName(contact.firstName || '');
    setContactLastName(contact.lastName || '');
    setContactEmail(contact.email || '');
    
    // Company remains as the selected company or current company name
    
    setContactPhone(contact.phone || '');
    setContactSignalStrength(contact.signalStrength || 2);
    setContactAvailableTimes(contact.availableTimes || '');
    setContactNotes(contact.notes || '');
  };
  
  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setCompanyName(company.name);
    setCompanySearchQuery(company.name);
    setShowCompanyDropdown(false);
  };
  
  const resetForm = () => {
    setContactFirstName('');
    setContactLastName('');
    setContactEmail('');
    setContactPhone('');
    setContactSignalStrength(2);
    setContactAvailableTimes('');
    setContactNotes('');
    setOriginalContact(null);
    setPendingSubmitData(null);
  };
  
  const isContactDataChanged = () => {
    if (!originalContact) return false;
    
    return (
      contactFirstName !== (originalContact.firstName || '') ||
      contactLastName !== (originalContact.lastName || '') ||
      contactEmail !== (originalContact.email || '') ||
      companyName !== (originalContact.company || '') ||
      contactPhone !== (originalContact.phone || '') ||
      contactSignalStrength !== (originalContact.signalStrength || 2) ||
      contactAvailableTimes !== (originalContact.availableTimes || '') ||
      contactNotes !== (originalContact.notes || '')
    );
  };
  
  const submitFastTrackContact = async (useOriginalData = false) => {
    setIsLoading(true);
    setError(null);
    setShowOverwriteConfirm(false);
    
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      // Determine which data to use
      const contactData = useOriginalData && originalContact ? {
        ...originalContact,
        jobId,
        companyId: selectedCompany?.id || companyId || originalContact.companyId, // Use provided companyId if available
        type: 'fast_track_connect',
      } : {
        firstName: contactFirstName,
        lastName: contactLastName,
        email: contactEmail,
        company: companyName,
        phone: contactPhone,
        signalStrength: contactSignalStrength,
        availableTimes: contactAvailableTimes,
        notes: contactNotes,
        jobId,
        userId,
        type: 'fast_track_connect',
      };
      
      // Add company ID if available
      if (selectedCompany?.id) {
        contactData.companyId = selectedCompany.id;
        
        // Add to companyIds array for multiple company associations
        if (!contactData.companyIds) {
          contactData.companyIds = [selectedCompany.id];
        } else if (!contactData.companyIds.includes(selectedCompany.id)) {
          contactData.companyIds.push(selectedCompany.id);
        }
      } else if (companyId) {
        contactData.companyId = companyId;
        
        // Add to companyIds array for multiple company associations
        if (!contactData.companyIds) {
          contactData.companyIds = [companyId];
        } else if (!contactData.companyIds.includes(companyId)) {
          contactData.companyIds.push(companyId);
        }
      }
      
      // If we have an original contact ID, use it
      if (originalContact && originalContact.id) {
        contactData.originalContactId = originalContact.id;
      }
      
      const result = await addFastTrackContact(contactData);
      
      if (result.success) {
        resetForm();
        // Pass the contact data to the callback
        onContactAdded && onContactAdded(result.contact || contactData);
        toast.success('Contact added successfully');
      } else {
        setError(result.error || 'Failed to add contact');
        toast.error(`Failed to add contact: ${result.error}`);
      }
    } catch (error) {
      console.error('Error adding fast track contact:', error);
      setError(error.message || 'An unexpected error occurred');
      toast.error('Failed to add contact');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!contactFirstName || !contactLastName || !contactEmail) {
      setError('First name, last name, and email are required');
      return;
    }
    
    // Check if we're modifying an existing contact
    if (originalContact && isContactDataChanged()) {
      setPendingSubmitData({ useOriginalData: false });
      setShowOverwriteConfirm(true);
    } else {
      submitFastTrackContact(false);
    }
  };
  
  // Company search input
  const CompanySelector = () => (
    <div className="relative">
      <div className="relative rounded-md shadow-sm">
        <input
          type="text"
          value={companySearchQuery}
          onChange={(e) => {
            setCompanySearchQuery(e.target.value);
            setShowCompanyDropdown(true);
          }}
          onFocus={() => setShowCompanyDropdown(true)}
          className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          placeholder="Search for a company..."
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoadingCompanies ? (
            <svg className="animate-spin h-5 w-5 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          )}
        </div>
      </div>
      
      {showCompanyDropdown && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white dark:bg-gray-800 shadow-lg max-h-60 overflow-auto ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {isLoadingCompanies ? (
            <div className="py-2 px-3 text-gray-500 dark:text-gray-400">Loading companies...</div>
          ) : filteredCompanies.length === 0 ? (
            <div className="py-2 px-3 text-gray-500 dark:text-gray-400">No companies found</div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredCompanies.map((company) => (
                <li
                  key={company.id}
                  onClick={() => handleCompanyClick(company)}
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 py-2 px-3"
                >
                  <div className="flex items-center">
                    {company.logo ? (
                      <div className="h-8 w-8 flex-shrink-0 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden mr-3">
                        <img 
                          src={company.logo} 
                          alt={company.name} 
                          className="h-full w-full object-cover" 
                        />
                      </div>
                    ) : (
                      <div className="h-8 w-8 flex-shrink-0 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-3">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {company.name.slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{company.name}</p>
                      {company.industry && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{company.industry} {company.size ? `• ${company.size}` : ''}</p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
  
  return (
    <>
      <Transition.Root show={true} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                      <BoltIcon className="h-6 w-6 text-purple-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        {companyId ? 'Add Company Contact' : 'Add Fast Track Contact'}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {companyId 
                            ? 'Add a contact for this company.'
                            : 'Add a contact that will be fast-tracked for this job position. You can select an existing contact or create a new one.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {error && (
                    <div className="mt-4 rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Error</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{error}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <form className="mt-5 sm:mt-4" onSubmit={handleSubmit}>
                    <ContactSelector
                      onSelectContact={handleContactSelect}
                      jobId={jobId}
                      companyId={companyId}
                      userId={getAuth().currentUser?.uid}
                    />
                    
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                      <div className="sm:col-span-3">
                        <label htmlFor="first-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          First name *
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="first-name"
                            id="first-name"
                            value={contactFirstName}
                            onChange={(e) => setContactFirstName(e.target.value)}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            required
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="last-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Last name *
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="last-name"
                            id="last-name"
                            value={contactLastName}
                            onChange={(e) => setContactLastName(e.target.value)}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            required
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-4">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Email address *
                        </label>
                        <div className="mt-1">
                          <input
                            id="email"
                            name="email"
                            type="email"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            required
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Company
                        </label>
                        <div className="mt-1">
                          <CompanySelector />
                        </div>
                        {selectedCompany && (
                          <div className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-md flex items-center">
                            {selectedCompany.logo ? (
                              <div className="h-8 w-8 flex-shrink-0 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden mr-3">
                                <img 
                                  src={selectedCompany.logo} 
                                  alt={selectedCompany.name} 
                                  className="h-full w-full object-cover" 
                                />
                              </div>
                            ) : (
                              <div className="h-8 w-8 flex-shrink-0 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mr-3">
                                <span className="text-xs font-medium text-purple-600 dark:text-purple-300">
                                  {selectedCompany.name.slice(0, 2).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Selected company</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Phone
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="phone"
                            id="phone"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="signal-strength" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Response Likelihood
                        </label>
                        <div className="mt-1">
                          <select
                            id="signal-strength"
                            name="signal-strength"
                            value={contactSignalStrength}
                            onChange={(e) => setContactSignalStrength(parseInt(e.target.value))}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          >
                            <option value={1}>Low</option>
                            <option value={2}>Medium</option>
                            <option value={3}>High</option>
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="available-times" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Available Times
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="available-times"
                            name="available-times"
                            rows={2}
                            value={contactAvailableTimes}
                            onChange={(e) => setContactAvailableTimes(e.target.value)}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          />
                        </div>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          When is this contact available for a call or meeting?
                        </p>
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Notes
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="notes"
                            name="notes"
                            rows={3}
                            value={contactNotes}
                            onChange={(e) => setContactNotes(e.target.value)}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          />
                        </div>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          Any additional information about this contact.
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        className="inline-flex w-full justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Adding Contact...
                          </>
                        ) : 'Add Contact'}
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                        onClick={onClose}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      
      {/* Overwrite Confirmation Dialog */}
      <Transition.Root show={showOverwriteConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowOverwriteConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Update Contact Information?
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You've made changes to an existing contact. Would you like to update their information or keep the original details?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 sm:ml-3 sm:w-auto"
                      onClick={() => submitFastTrackContact(false)}
                    >
                      Update Contact
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => submitFastTrackContact(true)}
                    >
                      Keep Original
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default FastTrackContactModal;
